/**
 * Hook for managing the in-app tutorial walkthrough.
 * Auto-triggers on first login (when trainingCompleted is false).
 * Provides startTour() for manual replay from the Tutorial page.
 */
import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './useAuth';
import { authService } from '../services/api';
import { getTourSteps, TourStep } from '../config/tourSteps';

interface UseTutorialReturn {
  /** Whether the tour is currently running */
  isRunning: boolean;
  /** Current step index */
  stepIndex: number;
  /** The tour steps for the current user's role */
  steps: TourStep[];
  /** Start the tour manually (e.g. from Tutorial page) */
  startTour: () => void;
  /** Stop / skip the tour */
  stopTour: () => void;
  /** Move to next step (called by TourProvider on step change) */
  handleStepChange: (index: number) => void;
  /** Called when tour finishes or is skipped */
  handleTourComplete: () => void;
  /** Whether this user still needs the auto-trigger tour */
  needsTour: boolean;
}

export function useTutorial(): UseTutorialReturn {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isRunning, setIsRunning] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);

  const role = user?.role || 'O';
  const steps = getTourSteps(role);
  const needsTour = !!user && user.trainingCompleted === false;

  // Auto-trigger tour on first login (after password change is handled)
  useEffect(() => {
    if (needsTour && !user?.mustChangePassword && !isRunning) {
      // Small delay to let the page render first
      const timer = setTimeout(() => {
        setStepIndex(0);
        setIsRunning(true);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [needsTour, user?.mustChangePassword]);

  const startTour = useCallback(() => {
    setStepIndex(0);
    // Navigate to the first step's route
    if (steps.length > 0 && steps[0].route) {
      navigate(steps[0].route);
    }
    // Delay to allow page to render
    setTimeout(() => setIsRunning(true), 500);
  }, [steps, navigate]);

  const stopTour = useCallback(() => {
    setIsRunning(false);
    setStepIndex(0);
  }, []);

  const handleStepChange = useCallback((index: number) => {
    const step = index < steps.length ? steps[index] : undefined;
    const targetRoute = step?.route;
    const hasTourEvent = !!step?.tourEvent;

    if (targetRoute || hasTourEvent) {
      // Pause joyride, update step index (triggers tourEvent context change),
      // navigate if needed, then resume after DOM settles
      setIsRunning(false);
      setStepIndex(index);
      if (targetRoute) navigate(targetRoute);
      setTimeout(() => {
        setIsRunning(true);
      }, hasTourEvent ? 600 : 400);
    } else {
      setStepIndex(index);
    }
  }, [steps, navigate]);

  const handleTourComplete = useCallback(async () => {
    setIsRunning(false);
    setStepIndex(0);

    // Navigate to daily queue after tour ends (delay to let state reset propagate)
    if (role === 'O' || role === 'operator') {
      setTimeout(() => navigate('/schedule?tab=daily'), 300);
    }

    // Mark training as completed in the backend
    if (needsTour) {
      try {
        await authService.completeTraining();
      } catch (err) {
        // Silently fail — user can still use the app
        console.error('Failed to mark training as completed:', err);
      }
    }
  }, [needsTour, role, navigate]);

  return {
    isRunning,
    stepIndex,
    steps,
    startTour,
    stopTour,
    handleStepChange,
    handleTourComplete,
    needsTour,
  };
}
